// @tco/shared-types - Shared TypeScript types and interfaces
// Common types used across web and mobile applications

// Auth types
export interface JwtPayload {
  sub: string
  email: string
  tenantId: string
  roleId: string
  exp?: number
  iat?: number
}
export interface LoginRequest {
  email: string
  password: string
}

export interface LoginResponse {
  accessToken: string
  refreshToken: string
  user: User
}

export interface RefreshRequest {
  refresh_token: string
}

export interface RefreshResponse {
  accessToken: string
  refreshToken: string
}

// User and Auth types
export interface User {
  id: string
  email: string
  phone?: string
  roleId: string
  tenantId: string
  isActive: boolean
  lastLoginAt?: string
  createdAt?: string
  updatedAt?: string
  // Related data from joins
  role?: Role
  employee?: {
    id: string
    firstName: string
    lastName: string
  }
}

// Helper to get display name
export function getUserDisplayName(user: User): string {
  if (user.employee) {
    return `${user.employee.firstName} ${user.employee.lastName}`
  }
  return user.email.split('@')[0]
}

export interface Role {
  id: string
  name: string
  permissions: RolePermissions
}

// JSONB permissions format from backend
// Examples: {"all": true} for admin, {"employees": ["read", "write"], "schedules": ["read"]} for custom roles
export type RolePermissions = Record<string, any>

// Common API response types
export interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: ApiError
  meta?: ApiMeta
}

export interface ApiError {
  code: string
  message: string
  details?: Record<string, unknown>
}

export interface ApiMeta {
  page?: number
  limit?: number
  total?: number
  totalPages?: number
}

// Entity base types
export interface BaseEntity {
  id: string
  createdAt: string
  updatedAt: string
}

export interface TenantEntity extends BaseEntity {
  tenantId: string
}

// Export type utilities
export type Nullable<T> = T | null
export type Optional<T> = T | undefined

// Tenant Registration types
export interface CreateTenantRequest {
  name: string
  inn: string
  domain: string
  admin_email: string
  admin_password: string
  admin_phone?: string
  subscription_plan_id?: string
}

export interface SubscriptionPlan {
  id: string
  name: string
  max_employees: number
  price: number
}

export interface TenantRegistrationResponse {
  tenant_id: string
  tenant_name: string
  domain: string
  access_token: string
  refresh_token: string
  subscription_plan: SubscriptionPlan
  trial_ends_at: string | null
  user: {
    id: string
    email: string
    role_name: string
  }
}

// Re-export notification types
export * from './notifications'
